import React from 'react';
import { Building2, ArrowLeft } from 'lucide-react';

export default function TermsOfServicePage({ setAuthPage }) {
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-300"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/70 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <button onClick={() => setAuthPage('landing')} className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
              <Building2 size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">FlipperPro</h1>
            </div>
          </button>
          <button
            onClick={() => setAuthPage('landing')}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} /> Back to Home
          </button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <h1 className="text-4xl font-bold text-white mb-2 tracking-tight">Terms of Service</h1>
        <p className="text-sm text-slate-500 mb-10">Last updated: March 1, 2026</p>

        <div className="space-y-8 text-[15px] leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using the FlipperPro web application (the "Service"), you agree to be
              bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may
              not access or use the Service. If you are using the Service on behalf of a company or
              other legal entity, you represent that you have the authority to bind that entity to
              these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Description of Service</h2>
            <p>
              FlipperPro is a project management and financial tracking platform designed for real
              estate investors and house flippers. The Service provides tools for deal analysis,
              project management, expense tracking, team collaboration, portfolio analytics, and
              third-party integrations (such as QuickBooks). The Service is provided "as is" and is
              intended for use as a business management tool, not as financial, legal, or investment
              advice.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. User Accounts</h2>
            <p className="mb-2">
              You must create an account to use the Service. You are responsible for maintaining the
              confidentiality of your account credentials and for all activities that occur under
              your account. You agree to provide accurate and complete information when creating
              your account and to update your information as necessary.
            </p>
            <p>
              You must notify us immediately if you become aware of any unauthorized use of your
              account. We are not liable for any loss or damage arising from your failure to protect
              your account credentials.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Teams and Workspaces</h2>
            <p className="mb-2">
              The Service allows you to create or join team workspaces. If you create a team, you
              are the "Team Owner" and are responsible for managing team membership, roles, and
              permissions. Team members can access shared project data within the workspace as
              defined by the role-based access controls configured by the Team Owner.
            </p>
            <p>
              Team Owners are responsible for ensuring that their team members comply with these
              Terms. Removing a team member revokes their access to the team workspace but does not
              delete their individual account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Acceptable Use</h2>
            <p className="mb-3">You agree not to:</p>
            <p className="mb-1">
              - Use the Service for any unlawful purpose or in violation of any applicable laws or
              regulations
            </p>
            <p className="mb-1">
              - Attempt to gain unauthorized access to any part of the Service, other user accounts,
              or related systems
            </p>
            <p className="mb-1">
              - Interfere with or disrupt the integrity or performance of the Service
            </p>
            <p className="mb-1">- Upload malicious code, viruses, or harmful content</p>
            <p className="mb-1">
              - Use the Service to store or transmit content that infringes on intellectual property
              rights
            </p>
            <p className="mb-1">
              - Reverse engineer, decompile, or disassemble any part of the Service
            </p>
            <p>
              - Share your account credentials with third parties or allow others to access your
              account
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              6. QuickBooks Online Integration
            </h2>
            <p className="mb-2">
              The Service integrates with Intuit QuickBooks Online ("QuickBooks") to provide
              financial data synchronization and expense management features. By connecting your
              QuickBooks account, you agree to the following:
            </p>
            <p className="mb-2">
              <span className="text-white font-medium">Authorization:</span> When you connect
              QuickBooks, you authorize FlipperPro to access your QuickBooks company data using
              Intuit's OAuth 2.0 protocol. This includes the ability to read your expenses, bills,
              vendors, and chart of accounts, as well as create, update, and void Purchase
              transactions on your behalf.
            </p>
            <p className="mb-2">
              <span className="text-white font-medium">Your Responsibilities:</span> You are
              responsible for ensuring that the QuickBooks account you connect is one you are
              authorized to use. You are responsible for reviewing any transactions pushed from
              FlipperPro to QuickBooks for accuracy. FlipperPro is not responsible for accounting
              errors, tax implications, or financial discrepancies resulting from synced data.
            </p>
            <p className="mb-2">
              <span className="text-white font-medium">Data Accuracy:</span> While we strive to
              accurately sync data between FlipperPro and QuickBooks, we do not guarantee that
              synced data will be free of errors. You should verify all financial data in your
              QuickBooks account after syncing. FlipperPro is not a substitute for professional
              accounting software practices or professional financial advice.
            </p>
            <p className="mb-2">
              <span className="text-white font-medium">Disconnection:</span> You may disconnect your
              QuickBooks account at any time. Upon disconnection, FlipperPro will immediately delete
              your stored OAuth tokens and cease all API communication with your QuickBooks account.
              Previously synced transaction data within FlipperPro may be retained as part of your
              project records unless you choose to delete it.
            </p>
            <p className="mb-2">
              <span className="text-white font-medium">Intuit's Terms:</span> Your use of QuickBooks
              is also governed by Intuit's Terms of Service and Privacy Policy. In the event of a
              conflict between these Terms and Intuit's terms regarding your use of QuickBooks,
              Intuit's terms shall prevail with respect to your QuickBooks account.
            </p>
            <p>
              <span className="text-white font-medium">Service Availability:</span> The QuickBooks
              integration depends on Intuit's APIs and infrastructure. We are not responsible for
              any downtime, changes, or discontinuation of Intuit's APIs that may affect the
              integration's functionality.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              7. Other Third-Party Integrations
            </h2>
            <p className="mb-2">
              The Service may integrate with additional third-party services, including but not
              limited to Google Maps and third-party property data providers. Your use of these integrations is
              subject to the respective third party's terms of service and privacy policies.
            </p>
            <p className="mb-2">
              When you connect a third-party service, you authorize FlipperPro to access and
              retrieve data from that service on your behalf. You may disconnect any integration at
              any time, which will revoke our access and delete associated tokens and cached data.
            </p>
            <p>
              We are not responsible for the availability, accuracy, or reliability of third-party
              services. Changes to third-party APIs or terms may affect the functionality of our
              integrations.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Data Ownership</h2>
            <p className="mb-2">
              You retain full ownership of all data you enter into the Service, including project
              details, financial records, documents, photos, and any other content ("Your Data"). We
              do not claim any ownership rights over Your Data.
            </p>
            <p>
              By using the Service, you grant us a limited, non-exclusive license to process, store,
              and display Your Data solely for the purpose of providing the Service to you. This
              license terminates when you delete Your Data or close your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              9. Financial Calculations and Projections
            </h2>
            <p>
              The Service provides financial calculators, ROI projections, deal analysis tools, and
              portfolio analytics. These tools are provided for informational purposes only and
              should not be relied upon as financial, investment, tax, or legal advice. Financial
              calculations are based on the data you provide and may not account for all variables
              that affect real estate transactions. You are solely responsible for verifying all
              financial projections and making your own investment decisions. FlipperPro is not
              liable for any financial losses resulting from reliance on the Service's calculations.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Service Availability</h2>
            <p>
              We strive to maintain high availability of the Service but do not guarantee
              uninterrupted access. The Service may be temporarily unavailable due to maintenance,
              updates, or circumstances beyond our control. We will make reasonable efforts to
              notify users of planned downtime when possible.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Limitation of Liability</h2>
            <p className="mb-2">
              To the maximum extent permitted by law, FlipperPro and its officers, directors,
              employees, and agents shall not be liable for any indirect, incidental, special,
              consequential, or punitive damages, including but not limited to loss of profits,
              data, business opportunities, or goodwill, arising from your use of or inability to
              use the Service.
            </p>
            <p>
              Our total cumulative liability to you for all claims arising from or related to the
              Service shall not exceed the amount you paid us in the twelve (12) months preceding
              the claim, or $100, whichever is greater.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Disclaimer of Warranties</h2>
            <p>
              The Service is provided on an "as is" and "as available" basis without warranties of
              any kind, whether express or implied, including but not limited to implied warranties
              of merchantability, fitness for a particular purpose, and non-infringement. We do not
              warrant that the Service will be error-free, secure, or available at all times, or
              that any defects will be corrected.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">13. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless FlipperPro and its officers,
              directors, employees, and agents from and against any claims, liabilities, damages,
              losses, and expenses (including reasonable attorneys' fees) arising from your use of
              the Service, your violation of these Terms, or your violation of any rights of a third
              party.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">14. Termination</h2>
            <p className="mb-2">
              You may terminate your account at any time by contacting us or through your account
              settings. We may suspend or terminate your access to the Service at any time, with or
              without cause, and with or without notice.
            </p>
            <p>
              Upon termination, your right to use the Service ceases immediately. We will retain
              your data for 30 days following account termination, after which it will be
              permanently deleted unless retention is required by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">15. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the
              State of Texas, without regard to its conflict of law provisions. Any legal action or
              proceeding arising from these Terms shall be brought exclusively in the state or
              federal courts located in Texas.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">16. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material
              changes by posting the updated Terms on this page and updating the "Last updated"
              date. Your continued use of the Service after changes are posted constitutes your
              acceptance of the revised Terms. If you do not agree with the changes, you must stop
              using the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">17. Contact Us</h2>
            <p>If you have questions about these Terms of Service, please contact us at:</p>
            <p className="mt-2 text-white font-medium">FlipperPro</p>
            <p>Email: legal@flipperpro.com</p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
              <Building2 size={16} className="text-white" />
            </div>
            <span className="text-sm text-slate-500">
              &copy; 2025 FlipperPro. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <button
              onClick={() => setAuthPage('privacy')}
              className="hover:text-white transition-colors"
            >
              Privacy
            </button>
            <span className="text-white font-medium">Terms</span>
            <button
              onClick={() => setAuthPage('landing')}
              className="hover:text-white transition-colors"
            >
              Home
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
